import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react'
import { concat } from 'lodash'
import { PlusIcon } from 'lucide-react'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Section from '@/components/base/section'
import Spinner from '@/components/base/spinner'
import Typography from '@/components/base/typography'
import NewsroomGrid from '@/components/sections/newsroom-grid'
import { useAppStore } from '@/contexts/store'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import ButtonMask from '../base/button-mask'
import { WPHeaders, WPTaxonomy } from '@/types'

export type ArchivePostTypesProps = HTMLAttributes<HTMLDivElement> & {
  post_type: string
  filter_by_taxonomy: boolean
  taxonomy: string
  taxonomy_term: string
  use_pagination: boolean
  per_page: string
  collection: any[]
  headers: WPHeaders
  filter_terms: WPTaxonomy[]
}

const ArchivePostTypes = ({
  collection: posts,
  headers: postHeaders,
  use_pagination,
  taxonomy_term,
  post_type,
  taxonomy,
  per_page
}: ArchivePostTypesProps) => {
  const { strings } = useAppStore()
  const [headers, setHeaders] = useState<WPHeaders>(postHeaders!)
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const [currentFilter, setCurrentFilter] = useState<WPTaxonomy>()
  const [collection, setCollection] = useState<any[]>(posts as any[])
  const [pagination, setPagination] = useState(-1)
  const [fetching, setFetching] = useState(false)
  const [needsPagination, setNeedsPagination] = useState(headers['X-WP-TotalPages'] > 1)
  const previousFilter = useRef<WPTaxonomy>(undefined)

  const getParams = useCallback(
    (page: number, filter?: WPTaxonomy) => {
      const perPage = use_pagination ? per_page : 1000
      const params = {
        per_page: perPage,
        page
      } as any

      if (filter) {
        post_type === 'post'
          ? (params['categories'] = filter?.term_id || '')
          : (params[`filter[${filter?.taxonomy || ''}]`] = filter?.slug || '')
      } else if (taxonomy_term) {
        params[`filter[${taxonomy || ''}]`] = taxonomy_term || ''
      }

      return {
        params
      }
    },
    [post_type, taxonomy, taxonomy_term, use_pagination, per_page]
  )

  const getPosts = useCallback(
    async (page: number, filter?: WPTaxonomy) => {
      const postType = post_type == 'post' ? 'posts' : post_type
      const needsReset = filter?.slug !== previousFilter.current?.slug

      setFetching(true)

      const { data, headers } = await getPromiseFromFetch({
        method: 'get',
        url: `${API.BASE}/${postType}`,
        params: getParams(page, filter)
      })

      setHeaders({
        'X-WP-Total': parseInt(headers['x-wp-total']),
        'X-WP-TotalPages': parseInt(headers['x-wp-totalpages'])
      })

      setCollection((previousCollection) => {
        return needsReset ? data : concat(previousCollection, data)
      })

      setNeedsPagination(page < parseInt(headers['x-wp-totalpages']))

      setFetching(false)
    },
    [post_type, getParams]
  )

  useEffect(() => {
    if (pagination < 0) return
    getPosts(pagination, currentFilter)
  }, [pagination, currentFilter, getPosts])

  return (
    <>
      <NewsroomGrid collection={collection} />

      {use_pagination && !needsPagination ? (
        <Section className="pt-[10rem]">
          <Container>
            <Button
              variant="primary"
              className="min-w-auto mx-auto w-full lg:w-auto"
              endIcon={
                fetching ? (
                  <Spinner variant="small" className="size-[1.6rem]" />
                ) : (
                  <PlusIcon size={16} />
                )
              }
              onClick={() => {
                setPagination((pagination) => {
                  previousFilter.current = currentFilter
                  return pagination < 0 ? 2 : pagination + 1
                })
              }}
            >
              <ButtonMask>
                <Typography variant="button">{strings?.labels?.load_more}</Typography>
              </ButtonMask>
            </Button>
          </Container>
        </Section>
      ) : null}
    </>
  )
}

export default ArchivePostTypes
